import type {
  CampaignStatus,
  ContactStatus,
  QualityRating,
  TemplateStatus,
  WhatsappAccountStatus,
  WhatsappTemplate,
} from '../../api/types';
import { Badge, type Tone } from '../ui';

/** Consent state, with the wording people expect on a contact list. */
export const CONTACT_STATUS_COPY: Record<ContactStatus, { label: string; tone: Tone }> = {
  SUBSCRIBED: { label: 'Subscribed', tone: 'ok' },
  PENDING: { label: 'No consent yet', tone: 'warn' },
  UNSUBSCRIBED: { label: 'Unsubscribed', tone: 'neutral' },
  BLOCKED: { label: 'Blocked by WhatsApp', tone: 'bad' },
};

export const TEMPLATE_STATUS_COPY: Record<TemplateStatus, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  PENDING: { label: 'In review', tone: 'warn' },
  APPROVED: { label: 'Approved', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'bad' },
  PAUSED: { label: 'Paused', tone: 'warn' },
  DISABLED: { label: 'Disabled', tone: 'bad' },
};

export const CAMPAIGN_STATUS_COPY: Record<CampaignStatus, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  SCHEDULED: { label: 'Scheduled', tone: 'warn' },
  SENDING: { label: 'Sending', tone: 'accent' },
  PAUSED: { label: 'Paused', tone: 'warn' },
  COMPLETED: { label: 'Completed', tone: 'ok' },
  CANCELED: { label: 'Canceled', tone: 'neutral' },
};

export const ACCOUNT_STATUS_COPY: Record<WhatsappAccountStatus, { label: string; tone: Tone }> = {
  CONNECTED: { label: 'Connected', tone: 'ok' },
  RESTRICTED: { label: 'Restricted by Meta', tone: 'bad' },
  DISCONNECTED: { label: 'Disconnected', tone: 'neutral' },
};

/** What the guard's reason strings mean to a person. */
export const REASON_COPY: Record<string, string> = {
  whatsapp_not_subscribed: 'no live WhatsApp plan',
  account_not_connected: 'no number connected',
  account_restricted: 'number restricted by Meta',
  account_disconnected: 'number disconnected',
  contact_not_found: 'unknown contact',
  contact_erased: 'contact erased',
  contact_pending: 'no consent recorded',
  contact_unsubscribed: 'unsubscribed',
  contact_blocked: 'blocked by WhatsApp',
  template_not_found: 'template missing',
  template_missing_optout: 'template has no opt-out',
  campaign_canceled: 'campaign canceled',
};

export function reasonLabel(reason: string): string {
  if (REASON_COPY[reason]) return REASON_COPY[reason];
  if (reason.startsWith('category_not_consented:')) {
    return `no consent for ${reason.split(':')[1]} messages`;
  }
  if (reason.startsWith('template_not_approved:')) return `template ${reason.split(':')[1]}`;
  if (reason.startsWith('plan_limit_broadcasts:')) {
    return `monthly limit of ${reason.split(':')[1]} reached`;
  }
  return reason.replace(/_/g, ' ');
}

export function ConsentBadge({ status }: { status: ContactStatus }) {
  const copy = CONTACT_STATUS_COPY[status];
  return <Badge tone={copy.tone}>{copy.label}</Badge>;
}

export function QualityBadge({ rating }: { rating: QualityRating }) {
  const tone: Tone =
    rating === 'GREEN' ? 'ok' : rating === 'YELLOW' ? 'warn' : rating === 'RED' ? 'bad' : 'neutral';
  const label =
    rating === 'GREEN'
      ? 'Quality high'
      : rating === 'YELLOW'
        ? 'Quality medium'
        : rating === 'RED'
          ? 'Quality low'
          : 'Quality unknown';
  return <Badge tone={tone}>{label}</Badge>;
}

/** The message as the recipient will see it, with sample values for `{{n}}`. */
export function WhatsAppPreview({
  template,
  body,
  header,
  footer,
  buttons,
  variables,
}: {
  template?: WhatsappTemplate | null;
  body?: string;
  header?: string | null;
  footer?: string | null;
  buttons?: string[];
  variables?: string[];
}) {
  const components = template?.components ?? [];
  const find = (type: string) => components.find((c) => c.type === type);
  const headerText =
    header ??
    (() => {
      const h = find('HEADER');
      if (!h) return null;
      return String(h.format ?? '') === 'TEXT' ? String(h.text ?? '') : `[${String(h.format)}]`;
    })();
  const footerText = footer ?? (find('FOOTER') ? String(find('FOOTER')!.text ?? '') : null);
  const buttonTexts =
    buttons ??
    ((find('BUTTONS')?.buttons as Array<{ text?: string }> | undefined) ?? []).map((b) =>
      String(b.text ?? ''),
    );
  let text = body ?? template?.body ?? '';
  (variables ?? []).forEach((v, i) => {
    text = text.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, 'g'), v || `{{${i + 1}}}`);
  });
  return (
    <div className="wa-preview" aria-label="Message preview">
      {headerText ? <div className="wa-preview__header">{headerText}</div> : null}
      <div>{text}</div>
      {footerText ? <div className="wa-preview__footer">{footerText}</div> : null}
      {buttonTexts.length ? (
        <div className="wa-preview__buttons">
          {buttonTexts.map((b, i) => (
            <div key={`${b}-${i}`} className="wa-preview__button">
              {b}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function pct(value: number | null): string {
  return value === null ? '—' : `${value}%`;
}
